import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { API_ENDPOINTS } from 'Constants/env';
import { ConfigurationResponse } from 'Interfaces/apiDtos';
import API from '~/api';
import { SipConfig } from '../types/sip.types';

export const QUERY_KEY_FETCH_SIP_CONFIG = 'softphone.fetchSipConfig';

/**
 * Fetches SIP configuration from the PersonConfig endpoint
 * @returns Promise<SipConfig> - Transformed SIP configuration
 * @throws Error if the API request fails or response is invalid
 */
export const fetchSipConfig = async (): Promise<SipConfig> => {
  try {
    const { data } = await API.get<ConfigurationResponse>(API_ENDPOINTS.Config);

    // Validate that webRtc configuration exists
    if (!data?.webRtc) {
      throw new Error('WebRTC configuration not found in API response');
    }

    const webRtc = data.webRtc;

    // Validate required fields
    if (
      !webRtc.uri ||
      !webRtc.authorizationUser ||
      !webRtc.password ||
      !webRtc.wsServers ||
      !webRtc.domain
    ) {
      throw new Error('Incomplete WebRTC configuration received from API');
    }

    // Transform API response to SipConfig type
    return {
      uri: webRtc.uri,
      authorizationUsername: webRtc.authorizationUser,
      authorizationPassword: webRtc.password,
      wsServers: webRtc.wsServers,
      domain: webRtc.domain,
    };
  } catch (error) {
    // Re-throw with more context for debugging
    if (error instanceof Error) {
      throw new Error(`Failed to fetch SIP configuration: ${error.message}`);
    }
    throw new Error('Failed to fetch SIP configuration: Unknown error');
  }
};

/**
 * React Query hook for fetching SIP configuration
 * Implements automatic retry logic with exponential backoff
 *
 * @param enabled - Whether the query should be enabled (default: true)
 * @returns UseQueryResult<SipConfig> - React Query result object
 *
 * @example
 * const { data: sipConfig, isLoading, isError, error } = useFetchSipConfig();
 * // Or with conditional fetching:
 * const { data: sipConfig } = useFetchSipConfig(isLoggedIn && isEnabled);
 */
export const useFetchSipConfig = (enabled = true) => {
  return useQuery<SipConfig, Error>({
    queryKey: [QUERY_KEY_FETCH_SIP_CONFIG],
    queryFn: fetchSipConfig,
    staleTime: 0, // Always consider data stale - fetch fresh on every mount
    gcTime: 0, // Don't keep in cache - always fetch fresh
    retry: 3, // Retry failed requests up to 3 times
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff: 1s, 2s, 4s (max 30s)
    enabled,
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnReconnect: true, // Refetch when network reconnects
    // Keep previous data during refetch to prevent race conditions
    // This ensures sipConfig never becomes undefined while refetching
    placeholderData: keepPreviousData,
  });
};

/**
 * Aggregator hook for softphone requests
 * Provides convenient access to all softphone-related queries
 */
export const useSoftphoneRequests = () => ({
  useFetchSipConfig,
});
